import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Order, Product, OrderStatus } from "../../interfaces/orders_manage";
import client, { client_product } from "../../graphql/client";
import { GET_ALL_ORDERS_QUERY, GET_ALL_ORDERS_USERID_QUERY, GET_ALL_PRODUCTS_QUERY } from "../../graphql/userQuery";
import { UPDATE_ORDER_STATUS } from "../../graphql/orderMutation";

const env = process.env.REACT_APP_ENV_SETTINGS || "dev";

interface Delivery {
  id: string;
  orderId: string;
  driverId?: string;
  vehicleId?: string;
  status: string;
  location?: any;
}

interface OrdersState {
  orders: Order[];
  products: Product[];
  deliveries: Delivery[];
  loading: boolean;
  error: string | null;
}

const initialState: OrdersState = {
  orders: [],
  products: [],
  deliveries: [],
  loading: false,
  error: null,
};



export const fetchOrdersAndProducts = createAsyncThunk(
  "orders/fetchAll",
  async () => {
    if (env === "dev") {
      const [ordersResponse, productsResponse] = await Promise.all([
        fetch("http://localhost:5000/getAllOrders"),
        fetch("http://localhost:5000/products"),
      ]);

      const ordersData = await ordersResponse.json();
      const productsData: Product[] = await productsResponse.json();

      return { ordersData: ordersData || [], productsData: productsData || [] };
    } else {
      const { data: ordersData } = await client.query({ query: GET_ALL_ORDERS_QUERY, fetchPolicy: "no-cache", });
      const { data: productsData } = await client_product.query({ query: GET_ALL_PRODUCTS_QUERY });

    //   console.log("Orders GraphQL Data:", ordersData);
    //   console.log("Products GraphQL Data:", productsData);

      return {
        ordersData: ordersData?.getAllOrders ?? [],
        productsData: productsData?.products ?? [],
      };
    }
  }
);



export const fetchOrdersAndProductsUsingid = createAsyncThunk(
  "orders/fetchByUser",
  async (userId: string) => {
    if (env === "dev") {
      const [ordersResponse, productsResponse] = await Promise.all([
        fetch(`http://localhost:5000/getOrdersByUser/${userId}`), // Assuming REST fallback
        fetch("http://localhost:5000/products"),
      ]);

      const ordersData = await ordersResponse.json();
      const productsData: Product[] = await productsResponse.json();

      return { ordersData: ordersData || [], productsData: productsData || [] };
    } else {
      const { data: ordersData } = await client.query({
        query: GET_ALL_ORDERS_USERID_QUERY,
        variables: { userId },
        fetchPolicy: "no-cache",
      });

      const { data: productsData } = await client_product.query({
        query: GET_ALL_PRODUCTS_QUERY,
        fetchPolicy: "no-cache",
      });

      return {
        ordersData: ordersData?.getOrders ?? [],
        productsData: productsData?.products ?? [],
      };
    }
  }
);


export const updateOrderStatus = createAsyncThunk(
  "orders/updateStatus",
  async ({ id, status }: { id: string; status: OrderStatus }) => {
    if (env === "dev") {
      await fetch(`http://localhost:5000/getAllOrders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      return { id, status };
    } else {
      await client.mutate({
        mutation: UPDATE_ORDER_STATUS,
        variables: { id, status },
      });
      return { id, status };
    }
  }
);

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Handle: fetchOrdersAndProducts (All)
    builder
      .addCase(fetchOrdersAndProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersAndProducts.fulfilled, (state, action) => {
        state.loading = false;
        const { ordersData, productsData } = action.payload;

        if (!Array.isArray(productsData) || !Array.isArray(ordersData)) {
          state.error = "Invalid products or orders data format.";
          return;
        }

        const productMap = new Map<string, Product>(
          productsData.map((p) => [String(p.id), p])
        );

        state.products = productsData;

        state.orders = ordersData.map((order: any) => ({
          ...order,
          orderType: order.orderType || "offline",
          items: (order.items || []).map((item: any) => {
            const productId = String(item.productId);
            const product = productMap.get(productId);
            return {
              ...item,
              productName: product?.name || `Unknown (${item.productId})`,
              vendorId: product?.vendorId,
              product,
            };
          }),
        }));
      })
      .addCase(fetchOrdersAndProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch data";
      });

    // Handle: fetchOrdersAndProductsUsingid (User-specific)
    builder
      .addCase(fetchOrdersAndProductsUsingid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersAndProductsUsingid.fulfilled, (state, action) => {
        state.loading = false;
        const { ordersData, productsData } = action.payload;

        if (!Array.isArray(productsData) || !Array.isArray(ordersData)) {
          state.error = "Invalid products or orders data format.";
          return;
        }

        const productMap = new Map<string, Product>(
          productsData.map((p) => [String(p.id), p])
        );

        state.products = productsData;

        state.orders = ordersData.map((order: any) => ({
          ...order,
          orderType: order.orderType || "offline",
          items: (order.items || []).map((item: any) => {
            const productId = String(item.productId);
            const product = productMap.get(productId);
            return {
              ...item,
              productName: product?.name || `Unknown (${item.productId})`,
              vendorId: product?.vendorId,
              product,
            };
          }),
        }));
      })
      .addCase(fetchOrdersAndProductsUsingid.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch user-specific orders";
      });
      

    // Handle: updateOrderStatus
    builder.addCase(updateOrderStatus.fulfilled, (state, action) => {
      const { id, status } = action.payload;
      state.orders = state.orders.map((order) =>
        order.id === id ? { ...order, status } : order
      );
    });

  },
});

export default ordersSlice.reducer;
